<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div
    class="password-view container"
    @keydown.enter="!isValid ? submit : undefined"
  >
    <Breadcrumbs class-name="py-3 mt-1" :path-list="pathList" />
    <div class="title-ssl-certificate-view text-start">
      <h2 class="fw-600">
        Instalação de Certificado Digital Financeiro e SSL com CSR
      </h2>
    </div>
    <div class="content-password-view">
      <div class="d-flex flex-row row">
        <div class="col-12 col-md-6 d-none d-lg-block text-center">
          <img
            alt="emitir-certificado"
            src="@/assets/images/instalar-certificado-ssl.svg"
          />
        </div>
        <div class="col-12 col-lg-6">
          <div class="description-password-view d-flex text-start">
            <p class="fs-6">Informe o nome do usuário e a senha de emissão</p>
          </div>
          <div class="input-create-user">
            <div class="input-label mb-1">
              <label for="user"> Usuário * </label>
              <Icon
                id="user-info-tooltip"
                name="circle-question"
                class="icon ms-2 align-text-bottom"
                :style="'line-height: 1.65rem'"
              />
            </div>
            <Input
              v-model="user"
              class="mb-5"
              icon="user"
              type="text"
              :is-required="true"
              placeholder="Ex: 01ED184010150778 11116521754247"
            />
          </div>
          <div class="input-create-password col-12">
            <div class="input-label mb-1">
              <label for="user"> Senha de emissão *</label>
              <Icon
                id="password-info-tooltip"
                name="circle-question"
                class="icon ms-2 align-text-bottom"
                :style="'line-height: 1.65rem'"
              />
            </div>
            <Input
              v-model="password"
              class="mb-5"
              icon="lock"
              type="password"
              :is-required="true"
              placeholder="Digite sua senha aqui"
              :error-log="error"
            />
          </div>
          <div class="d-flex justify-content-end mt-2">
            <button
              id="next-step-button"
              class="btn"
              type="button"
              :class="[!isValid ? 'btn-secondary' : 'btn-primary']"
              :disabled="!isValid"
              @click="submit"
            >
              <span>Avançar</span>
              <Icon class="ms-2" name="arrow-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <Contact />
  </div>
  <Loading :is-loading="isLoading" />
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { useRoute } from 'vue-router'

  import Contact from '@/components/Molecules/Contact.vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import Input from '@/components/Atoms/Input.vue'
  import Loading from '@/components/Atoms/Loading.vue'
  import { EPanel } from '@/helpers/EPanel.enum'
  import { mapActions, mapState } from 'pinia'
  import { useDynamicForms } from '@/store/Pinia/useDynamicForms.store'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import { useAR } from '@/store/Pinia/useAR.store'
  import { panelController } from '@/infra/SSLCertificate/panel.controller'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import { useSSLCertificate } from '@/store/Pinia/useSSLCertificate.store'
  import {
    DomainsValidationType,
    domainValidationController,
  } from '@/infra/DomainValidation/domainValidation.controller'
  import { Tooltip } from 'bootstrap'
  import emissionController from '@/infra/BirdId/controllers/emission.controller'
  import { ESolicitationStatus } from '@/helpers/ESisar.enum'
  import { useOeRecaptcha } from '@/utils/useOeRecaptcha'

  export default defineComponent({
    name: 'HomeView',
    components: {
      Contact,
      Icon,
      Input,
      Loading,
    },
    setup() {
      return {
        oeRecaptcha: useOeRecaptcha(),
        route: useRoute(),
      }
    },
    data: () => ({
      user: '',
      password: '',
      error: '',
      userTooltip: null as Tooltip | null,
      passwordTooltip: null as Tooltip | null,
      isLoading: false,
    }),
    computed: {
      ...mapState(useAR, ['arData']),
      ...mapState(useSSLCertificate, ['sslCertificate']),
      isValid() {
        return !!this.user && !!this.password && !this.error
      },
      userTooltipText() {
        return 'Você pode encontrar seu código de usuário em seu e-mail com o texto: “Emissão de Certificado Digital APROVADA” no assunto.'
      },
      passwordTooltipText() {
        return 'A Senha de Emissão foi criada por você no momento do preenchimento dos dados do seu Certificado.'
      },
      pathList() {
        return {
          'Guia de instalação SSL': 'ssl-certificate/installation-guide-ssl',
          'Downolad de Certificado Digital com CSR': '',
        }
      },
    },
    methods: {
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      ...mapActions(useSSLCertificate, ['setUser', 'setDigitalTermHash']),
      ...mapActions(useDynamicForms, ['setPassword']),
      ...mapActions(useSolicitation, ['setCodigoSolicitacao']),
      async submit() {
        try {
          this.isLoading = true
          if (this.error === '' && this.isValid && this.password) {
            this.$router.push('')
          }

          this.setUser(this.user.trim())
          this.setCodigoSolicitacao(this.user.split(' ')[0])
          this.setPassword(this.password)

          const recaptchaTokenGetSolicitationData =
            await this.oeRecaptcha.getToken('getsolicitationdata')

          const solicitation = await emissionController.getSolicitationData({
            arsubdomain: this.arData.subdomain || '',
            solicitacao: this.user.split(' ')[0],
            recaptcha_token: recaptchaTokenGetSolicitationData,
          })

          if (solicitation.produto.isSSL) {
            const recaptchaTokenGetDomainsValidation =
              await this.oeRecaptcha.getToken('isvaliddomain')

            const response =
              await domainValidationController.getDomainsValidation({
                codigo: this.user.split(' ')[0].trim(),
                senha: this.password,
                recaptcha_token: recaptchaTokenGetDomainsValidation,
              })

            if (!this.checkDomainValidity(response.domainsValidation)) {
              return this.$router.push('/domain-validation/validation')
            }
          }

          const statusSolicitacao = Number(solicitation.situacao.id)

          if (
            // eslint-disable-next-line operator-linebreak
            statusSolicitacao !== ESolicitationStatus.APROVADA &&
            statusSolicitacao !== ESolicitationStatus.EMITIDA
          ) {
            throw new Error(
              `Solicitação ainda não está pronta para emissão. Status:
        ${solicitation.situacao.descricao}`,
            )
          }

          const recaptchaTokenPanelValidate = await this.oeRecaptcha.getToken(
            'panelvalidate',
          )

          const validateResponse = await panelController.validate({
            password: this.password,
            solicitation: this.user,
            arsubdomain: this.arData.subdomain || '',
            recaptcha_token: recaptchaTokenPanelValidate,
          })

          if (validateResponse.mensagem.erro) {
            throw new Error(validateResponse.mensagem.erro.mensagem)
          }

          switch (validateResponse.mensagem.emissao?.status) {
            case EPanel.EStatus.AGUARDANDO_CSR:
              this.setDigitalTermHash(
                validateResponse.mensagem.emissao.digitalterm_hash || '',
              )
              this.$router.push('/ssl-certificate/csr-signature')
              break
            case EPanel.EStatus.CERTIFICADO_JA_EMITIDO:
              this.$router.push('/ssl-certificate/csr-download')
              break
            default:
              this.toggleToastGlobal({
                isShow: true,
                type: 'info',
                message:
                  // eslint-disable-next-line operator-linebreak
                  validateResponse.mensagem.emissao?.obs ||
                  'Erro ao validar solicitação, entre em contato com o suporte.',
              })
              break
          }

          return null
        } catch (error) {
          const err = error as ErrorEvent
          return this.toggleToastGlobal({
            type: 'danger',
            isShow: true,
            message: err.message,
          })
        } finally {
          this.isLoading = false
        }
      },
      checkDomainValidity(domainsValidation: DomainsValidationType[]): boolean {
        let allArevalid = true

        domainsValidation.forEach((domainObject) => {
          if (!domainObject.verified_at) {
            allArevalid = false
          }
        })

        return allArevalid
      },
    },
    mounted() {
      this.oeRecaptcha.showBadge()

      this.user = this.sslCertificate.user

      this.userTooltip = new Tooltip('#user-info-tooltip', {
        title: this.userTooltipText,
        placement: 'right',
      })

      this.passwordTooltip = new Tooltip('#password-info-tooltip', {
        title: this.passwordTooltipText,
        placement: 'right',
      })
    },
    beforeMount() {
      if (this.route.query.solicitation) {
        this.sslCertificate.user = String(
          this.route.query.solicitation,
        ).replace('+', ' ')
      }
    },
    async beforeRouteEnter(to, from, next) {
      const sslStore = useSSLCertificate()
      const dynamicFormsStore = useDynamicForms()
      const arStore = useAR()
      const toast = useToastGlobal()

      const { user } = sslStore.$state.sslCertificate
      const { senha } = dynamicFormsStore.$state.formData
      const { subdomain } = arStore.$state.arData

      if (user !== '' && senha !== '') {
        try {
          const oeRecaptcha = useOeRecaptcha()
          const recaptchaTokenGetSolicitationData = await oeRecaptcha.getToken(
            'panelvalidate',
          )

          const response = await panelController.validate({
            password: senha,
            solicitation: user,
            arsubdomain: subdomain || '',
            recaptcha_token: recaptchaTokenGetSolicitationData,
          })

          if (
            response.mensagem.emissao?.status === EPanel.EStatus.AGUARDANDO_CSR
          ) {
            return next('/ssl-certificate/csr-signature')
          }

          return next()
        } catch (error) {
          toast.toggleToastGlobal({
            isShow: true,
            message: 'Erro ao validar informações, tente novamente mais tarde',
          })
        }
      }

      return next()
    },
    beforeRouteLeave() {
      this.oeRecaptcha.hideBadge()
    },
  })
</script>

<style lang="stylus" scoped>
  .title-ssl-certificate-view
    font-size: 24px
    padding-bottom: 1.5rem

  .input-label
    width: 100%;
    text-align: left;
    display: flex
    align-items: center

  @media screen and (max-width: 992px)
    #next-step-button
      width: 100%
</style>
